<template>
    <portal>
        <div class="mb-8 d-flex align-center">
            <v-icon color="primary" size="30">mdi-cogs</v-icon>
            <h1 class="headline ml-4 font-weight-bold grey--text--darken-2 text-uppercase">Settings</h1>
        </div>

        <v-form
            class="pa-4"
            ref="form"
            v-model="valid"
        >

            <v-card class="pa-8">

                <v-text-field
                    v-model="userForm.fname"
                    :rules="fnameRules"
                    label="First Name"
                    required
                ></v-text-field>

                <v-switch v-model="isMarried" label="Married"></v-switch>

                <v-text-field
                    v-if="isMarried"
                    v-model="spouseName"
                    :rules="spouseNameRules"
                    label="Spouse First Name"
                    required
                ></v-text-field>

                <v-text-field
                    v-model="userForm.lname"
                    :rules="lnameRules"
                    label="Last Name"
                    required
                ></v-text-field>

                <v-text-field
                    v-model="userForm.email"
                    :rules="emailRules"
                    label="E-mail"
                    required
                ></v-text-field>

                <v-text-field
                    v-model="userForm.phone"
                    label="Phone"
                    required
                ></v-text-field>

                <v-text-field
                    v-model="userForm.address"
                    label="Address"
                    required
                ></v-text-field>

                <v-switch v-model="userForm.change_password" label="Change Password"></v-switch>

                <v-text-field
                    v-if="userForm.change_password"
                    v-model="userForm.password"
                    :rules="passwordRules"
                    label="Password"
                    type="password"
                    required
                ></v-text-field>

                <v-text-field
                    v-if="userForm.change_password"
                    v-model="userForm.password_confirmation"
                    :rules="passwordConfirmationRules"
                    label="Password confirmation"
                    type="password"
                    required
                ></v-text-field>

                <v-switch v-model="userForm.double_authentication_on" label="Two-Factor Authentication"></v-switch>
                <v-switch v-model="subscribed" label="Subscribed"></v-switch>

                <v-card-actions>
                    <v-btn
                        :disabled="!valid"
                        class="d-flex mx-auto"
                        @click="update"
                        color="primary"
                        depressed
                        rounded
                        height="48"
                        width="200"
                    >
                        Update
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-form>

    </portal>
</template>

<script>
import Portal from "@/components/Portal";
import {mapGetters, mapState} from "vuex";
import axios from "axios";

export default {
    name: "Settings",
    components: {
        Portal
    },
    data: () => ({
        subscribed: true,
        valid: true,
        isMarried: false,
        spouseName: '',
        userForm: {
            fname: '',
            lname: '',
            spouseFname: '',
            email: '',
            password: '',
            password_confirmation: '',
            phone: '',
            address: '',
            change_password: false,
            double_authentication_on: false
        },
        fnameRules: [
            v => !!v || 'First Name is required',
        ],
        lnameRules: [
            v => !!v || 'Last Name is required',
        ],
        passwordRules: [
            v => !!v || 'Password is required',
            v => v && v.length >= 6 || 'The password must be at least 6 characters',
        ],
        emailRules: [
            v => !!v || 'E-mail is required',
            v => /.+@.+\..+/.test(v) || 'E-mail must be valid',
        ],
    }),
    computed: {
        ...mapGetters('auth', [
            'user',
        ]),
        ...mapGetters('individuals', [
            'personOne',
            'personTwo',
            'personTwoExists',
        ]),
        ...mapState('household', ['household']),
        passwordConfirmationRules() {
            return [
                v => !!v || 'Password confirmation is required',
                (this.userForm.password === this.userForm.password_confirmation) || 'Passwords must match',
                v => v && v.length >= 6 || 'The password must be at least 6 characters',
            ]
        },
        spouseNameRules() {
            return [
                v => (!!v && this.isMarried) || 'Spouse Name is required',
            ]
        },
    },
    mounted() {
        this.loadIndividuals();

        axios.get('api/users/' + this.user.id)
            .then(response => {
                let userData = response.data.data.user;
                this.userForm.fname = userData.fname;
                this.userForm.lname = userData.lname;
                this.userForm.email = userData.email;
                this.userForm.phone = userData.phone;
                this.userForm.address = userData.address;
                this.userForm.double_authentication_on = userData.double_authentication_on;
            });
    },
    methods: {
        update() {
            this.$refs.form.validate();

            if (! this.valid) {
                return;
            }

            Promise.resolve([
                this.saveIndividuals(),
                this.saveUser()
            ]).then(() => {
                this.$noty.success('Changes are saved successfully')
            }).catch(() => {
                this.$noty.error('Sorry, changes failed to save')
            })
        },
        saveIndividuals() {
            this.$store.commit('individuals/setPersonOneName', this.userForm.fname);

            // Delete spouse if it existed but the toggle was switched off
            if (! this.isMarried && this.personTwoExists) {
                this.$store.dispatch('individuals/deleteIndividual', { individual: this.personTwo, index: 1 });
            }

            this.$store.commit('individuals/addRemovePersonTwo', this.isMarried);

            if (this.isMarried) {
                this.$store.commit('individuals/setPersonTwoName', this.spouseName);
            }

            return this.$store.dispatch('individuals/saveIndividuals')
                .then(() => this.loadIndividualsCallback());
        },
        loadIndividuals() {
            this.$store.dispatch('individuals/loadIndividuals')
                .then(() => this.loadIndividualsCallback());
        },
        loadIndividualsCallback() {
            this.isMarried = this.personTwoExists;
            this.spouseName = this.personTwo.name;
        },
        saveUser() {
            let vm = this;

            return axios.put('api/users/' + this.user.id, this.userForm, { showLoader: false })
                .then(response => {
                    let userData = {
                        id: response.data.data.user.id,
                        fname: response.data.data.user.fname,
                        lname: response.data.data.user.lname,
                        email: response.data.data.user.email,
                        phone: response.data.data.user.phone,
                        address: response.data.data.user.address,
                        household_id: response.data.data.user.household_id,
                        terms_accepted: response.user.terms_accepted,
                        has_external_household: vm.user.has_external_household,
                        double_authentication_on: response.double_authentication_on
                    };
                    this.$store.commit('auth/setUserData', userData);
                });
        }
    }
}
</script>

